'use no memo'

import { useTranslator } from '@/hooks/useTranslator'
import { Grid, Typography, Button } from '@mui/material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useState, useEffect } from 'react'
import { FormInput } from '../form'
import InputDateField from '../common/input/InputDateField'
import PhoneCodeSelection from '../selection/PhoneCodeSelection'
import BankSelection from '../selection/BankSelection'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  ClosingDateTypeEnum,
  getCompanyCreateSchema,
  IHttpError,
  PaymentPartnerEnum,
  useCompanyCreateMutation
} from '@/@data'
import { useNotification } from '@/hooks'
import FormFileInput from '../form/FormFileInput'
import { ErrorAlert } from '../common/ErrorAlert'
import FormSelect from '../form/FormSelect'
import dayjs from 'dayjs'

const EmployerManualForm = ({ onClose, refetch }: { onClose: () => void; refetch: () => void }) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const { mutate: createCompany } = useCompanyCreateMutation()

  const [showError, setShowError] = useState(false)
  const [error, setError] = useState<IHttpError | null>(null)

  const closingDateOptions = [
    { value: 'end_month', label: __('End of Month') },
    { value: 'first_month', label: __('First of Month') },
    { value: 'enter_date', label: __('Enter Date') }
  ]

  const closingDateTypeOptions = [
    { value: 'before', label: __('Previous Month') },
    { value: 'after', label: __('Next Month') }
  ]
  const withdrawCutoffOptions = [
    { value: 'end_month', label: __('End of Month') },
    { value: 'enter_date', label: __('Enter Date') }
  ]
  
  const paymentPartnerOptions = [
    { value: PaymentPartnerEnum.VnptEpay, label: 'VNPT ePay' },
    { value: PaymentPartnerEnum.Shinhan, label: 'Shinhan Bank' }
  ]

  const methods = useForm<any>({
    defaultValues: {
      logo: null,
      name: '',
      short_name: '',
      company_type: '',
      email: '',
      bank_id: null,
      address: '',
      establish_date: '',
      poc_phone_code: '',
      poc_phone: '',
      poc_firstname: '',
      phone: '',
      business_number: '',
      bank_account_number: '',
      closing_date: '',
      closing_specific_date: '',
      closing_date_type: '',
      minimum_working_days: 0,
      withdraw_cutoff_option: 'end_month',
      withdraw_cutoff_day: '',
      memo: '',
      payment_partner: PaymentPartnerEnum.VnptEpay
    },
    mode: 'onChange',
    resolver: yupResolver(getCompanyCreateSchema(__) as any)
  })

  // const {
  //   formState: { isValid }
  // } = methods

  const onSubmit = (data: any) => {
    const closingDateType: Record<string, string> = {
      end_month: 'before',
      first_month: 'after',
      enter_date: data.closing_date_type
    }

    const normalizedData = {
      ...data,
      closing_date: Number(data.closing_date),
      minimum_working_days: Number(data.minimum_working_days),
      withdraw_cutoff_day:
        data.withdraw_cutoff_option === 'end_month'
          ? 0
          : Number(data.withdraw_cutoff_day),
      closing_date_type: closingDateType[data.closing_specific_date]
    }

    delete normalizedData.closing_specific_date
    delete normalizedData.withdraw_cutoff_option

    const formData = new FormData()

    Object.entries(normalizedData).forEach(([key, value]) => {
      if (key === 'logo' && value instanceof FileList && value.length > 0) {
        formData.append('logo', value[0])
      } else if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value))
        } else {
          formData.append(key, String(value))
        }
      }
    })

    createCompany(
      { data: formData as any },
      {
        onSuccess: () => {
          n.success(__('Company created successfully'))
          onClose()
          refetch()
        },
        onError: (error: IHttpError) => {
          setError(error)
        }
      }
    )
  }

  useEffect(() => {
    if (!showError) return

    const subscription = methods.watch((_, { name }) => {
      if (name === 'name') {
        setShowError(false)
      }
    })

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe()
      }
    }
  }, [showError, methods])

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'closing_specific_date') {
        const selectedOption = value.closing_specific_date

        if (selectedOption === 'end_month') {
          methods.setValue('closing_date', '0')
          methods.setValue('closing_date_type', 'before')
        } else if (selectedOption === 'first_month') {
          methods.setValue('closing_date', '1')
          methods.setValue('closing_date_type', 'after')
        } else if (selectedOption === 'enter_date') {
          methods.setValue('closing_date', '')
          methods.setValue('closing_date_type', '')
        }
      }
      if (name === 'withdraw_cutoff_option') {
        if (value.withdraw_cutoff_option === 'end_month') {
          methods.setValue('withdraw_cutoff_day', '')
        }
      }
    })

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe()
      }
    }
  }, [methods])

  useEffect(() => {
    methods.reset({
      memo: __('Ngày làm việc')
    })
  }, [methods])

  const getRangeWorkingDay = (
    closingDate: number,
    closingDateType: ClosingDateTypeEnum,
    payPeriod: string
  ): {
    startDate: string
    endDate: string
  } => {
    const firstDayOfMonth = dayjs(payPeriod).format('YYYY-MM-01')

    if (closingDate === 0) {
      if (closingDateType === ClosingDateTypeEnum.Before) {
        return {
          startDate: firstDayOfMonth,
          endDate: dayjs(firstDayOfMonth).endOf('month').format('YYYY-MM-DD')
        }
      } else {
        const nextMonth = dayjs(firstDayOfMonth).add(1, 'month')

        return {
          startDate: nextMonth.format('YYYY-MM-DD'),
          endDate: nextMonth.endOf('month').format('YYYY-MM-DD')
        }
      }
    }

    const closingDateForMonth = dayjs(firstDayOfMonth).set('date', closingDate).format('YYYY-MM-DD')

    if (closingDateType === ClosingDateTypeEnum.After) {
      return {
        startDate: dayjs(closingDateForMonth).add(1, 'day').format('YYYY-MM-DD'),
        endDate: dayjs(closingDateForMonth).add(1, 'month').format('YYYY-MM-DD')
      }
    } else {
      return {
        startDate: dayjs(closingDateForMonth).subtract(1, 'month').add(1, 'day').format('YYYY-MM-DD'),
        endDate: closingDateForMonth
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <ErrorAlert error={error} onClose={() => setShowError(false)} />
        <div className='overflow-y-auto'>
          <style>
            {`
              .MuiPopover-root,
              .MuiMenu-root,
              .MuiPopper-root,
              .MuiPaper-root.MuiPopover-paper,
              .MuiPaper-root.MuiMenu-paper {
                z-index: 9999 !important;
              }
            `}
          </style>
          <Grid container spacing={4} rowSpacing={5}>
            <Grid size={{ xs: 12 }}>
              <FormFileInput
                name='logo'
                label={__('Logo Employer')}
                placeholder={__('Import File')}
                fullWidth
                control={methods.control}
                accept='image/*'
                onClear={() => methods.setValue('logo', null)}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Employer Full Name')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <FormInput
                name='name'
                placeholder={__('Enter Full Name Employer')}
                control={methods.control}
                onClear={() => methods.setValue('name', '')}
                fullWidth
                onChange={e => {
                  const value = e.target.value

                  if (value.startsWith(' ')) {
                    methods.setValue('name', value.trimStart())
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Employer Short Name')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <FormInput
                name='short_name'
                placeholder={__('Enter Short Name Employer')}
                control={methods.control}
                onClear={() => methods.setValue('short_name', '')}
                fullWidth
                onChange={e => {
                  const value = e.target.value

                  if (value.startsWith(' ')) {
                    methods.setValue('short_name', value.trimStart())
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormInput
                label={__('Employer Type')}
                name='company_type'
                placeholder={__('Enter Employer Type')}
                control={methods.control}
                onClear={() => methods.setValue('company_type', '')}
                fullWidth
                onChange={e => {
                  const value = e.target.value

                  if (value.startsWith(' ')) {
                    methods.setValue('company_type', value.trimStart())
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Controller
                name='establish_date'
                control={methods.control}
                render={({ field }) => (
                  <InputDateField
                    {...field}
                    name='establish_date'
                    value={methods.getValues('establish_date')}
                    placeholder={__('Select Date')}
                    label={__('Establish Date')}
                    onChange={value => methods.setValue('establish_date', value)}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormInput
                label={__('POC Name')}
                name='poc_firstname'
                placeholder={__('Enter POC Name')}
                control={methods.control}
                onClear={() => methods.setValue('poc_firstname', '')}
                fullWidth
                onChange={e => {
                  const value = e.target.value

                  if (value.startsWith(' ')) {
                    methods.setValue('poc_firstname', value.trimStart())
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography className='text-[15px] font-medium leading-[15px] mb-1 text-[#2f2b3de6]'>
                {__('POC Phone')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }} className='custom-field'>
                  <Controller
                    name='poc_phone_code'
                    control={methods.control}
                    render={({ field }) => (
                      <PhoneCodeSelection
                        {...field}
                        placeholder={__('Country')}
                        onClear={() => methods.setValue('poc_phone_code', '')}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 8 }}>
                  <FormInput
                    name='poc_phone'
                    placeholder={__('Enter Phone')}
                    type='number'
                    control={methods.control}
                    fullWidth
                    onClear={() => methods.setValue('poc_phone', '')}
                    onChange={e => {
                      const value = e.target.value

                      if (value.startsWith(' ')) {
                        methods.setValue('poc_phone', value.trimStart())
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormInput
                type='email'
                label={__('POC Email')}
                name='email'
                placeholder={__('Enter POC Email')}
                control={methods.control}
                onClear={() => methods.setValue('email', '')}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Employer Registration')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <FormInput
                name='business_number'
                placeholder={__('Enter Employer Registration')}
                control={methods.control}
                onClear={() => methods.setValue('business_number', '')}
                fullWidth
                onChange={e => {
                  const value = e.target.value

                  if (value.startsWith(' ')) {
                    methods.setValue('business_number', value.trimStart())
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Bank Name')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <Controller
                name='bank_id'
                control={methods.control}
                render={({ field, fieldState }) => (
                  <div className='custom-field'>
                    <BankSelection
                      {...field}
                      fullWidth
                      placeholder={__('Select Bank')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      onClear={() => methods.setValue('bank_id', null)}
                    />
                  </div>
                )}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Bank Account')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <FormInput
                name='bank_account_number'
                placeholder={__('Enter Bank Account')}
                control={methods.control}
                onClear={() => methods.setValue('bank_account_number', '')}
                fullWidth
                onChange={e => {
                  const value = e.target.value

                  if (value.startsWith(' ')) {
                    methods.setValue('bank_account_number', value.trimStart())
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Payment Partner')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <FormSelect
                name='payment_partner'
                options={paymentPartnerOptions}
                placeholder={__('Select Payment Partner')}
                height='36.6px'
                onClear={() => methods.setValue('payment_partner', PaymentPartnerEnum.VnptEpay)}
                className='bg-[#fff]'
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormInput
                label={__('Employer Address')}
                name='address'
                placeholder={__('Enter Address')}
                control={methods.control}
                onClear={() => methods.setValue('address', '')}
                fullWidth
                onChange={e => {
                  const value = e.target.value

                  if (value.startsWith(' ')) {
                    methods.setValue('address', value.trimStart())
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Timesheet Closing Date')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <div
                className={`grid ${methods.watch('closing_specific_date') !== 'enter_date' ? 'grid-cols-1' : 'grid-cols-2'}  gap-4`}
              >
                <div>
                  <div
                    className={`grid ${methods.watch('closing_specific_date') === 'enter_date' ? 'grid-cols-2' : 'grid-cols-1'} gap-1`}
                  >
                    <div>
                      <FormSelect
                        name='closing_specific_date'
                        placeholder={__('Choose Options')}
                        options={closingDateOptions}
                        height='36.6px'
                        onClear={() => {
                          methods.setValue('closing_date', '')
                          methods.setValue('closing_specific_date', '')
                        }}
                        onChange={() => {
                          methods.clearErrors()
                        }}
                      />
                    </div>
                    <div className={`${methods.watch('closing_specific_date') === 'enter_date' ? 'block' : 'hidden'}`}>
                      <Controller
                        name='closing_date'
                        control={methods.control}
                        render={({ field }) => {
                          const closingDateOption = methods.watch('closing_specific_date')
                          const isDisabled = closingDateOption !== 'enter_date'

                          return (
                            <FormInput
                              {...field}
                              name='closing_date'
                              placeholder={__('Enter Date')}
                              control={methods.control}
                              disabled={isDisabled}
                              type='number'
                              slotProps={{
                                input: {
                                  endAdornment: <span className='text-[#2F2B3D66] text-[14px] ml-2'>{__('Day')}</span>
                                }
                              }}
                            />
                          )
                        }}
                      />
                    </div>
                  </div>
                  {(() => {
                    const closingSpecificDate = methods.watch('closing_specific_date')

                    const closingDateType =
                      closingSpecificDate === 'end_month'
                        ? 'before'
                        : closingSpecificDate === 'first_month'
                          ? 'after'
                          : methods.watch('closing_date_type')

                    const closingDate = methods.watch('closing_date')

                    if (!closingSpecificDate || !closingDateType) {
                      return false
                    }

                    if (closingSpecificDate === 'enter_date' && (!closingDate || closingDate === '')) {
                      return false
                    }

                    return true
                  })() && (
                    <div className='text-[13px] font-normal text-[#2f2b3d8c] mt-1'>
                      {(() => {
                        const closingSpecificDate = methods.watch('closing_specific_date')
                        const closingDateType = methods.watch('closing_date_type')
                        const closingDate = methods.watch('closing_date')

                        let dateValue = 0

                        if (closingSpecificDate === 'end_month') {
                          dateValue = 0 // End of month
                        } else if (closingSpecificDate === 'first_month') {
                          dateValue = 1 // First of month
                        } else if (closingSpecificDate === 'enter_date') {
                          dateValue = Number(closingDate) || 0
                        }

                        const range = getRangeWorkingDay(dateValue, closingDateType, dayjs().format('YYYY-MM-DD'))

                        return `${__('VD: Kỳ lương Tháng')} ${dayjs().month() + 1}/${dayjs().year()} : ${range.startDate} → ${range.endDate}`
                      })()}
                    </div>
                  )}
                </div>
                <div className={`${methods.watch('closing_specific_date') !== 'enter_date' ? 'hidden' : 'block'}`}>
                  <FormSelect
                    name='closing_date_type'
                    placeholder={__('Choose Closing Date Type')}
                    options={closingDateTypeOptions}
                    height='36.6px'
                    onClear={() => methods.setValue('closing_date_type', '')}
                  />
                </div>
              </div>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Minimum Working Days')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <FormInput
                name='minimum_working_days'
                placeholder={__('Enter Minimum Working Days can withdraw')}
                control={methods.control}
                onClear={() => methods.setValue('minimum_working_days', '')}
                fullWidth
                type='number'
                slotProps={{
                  input: {
                    inputProps: {
                      min: 0,
                      max: 30,
                      step: 1
                    }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Memo')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <FormInput
                name='memo'
                placeholder={__('Enter reason to show on app')}
                control={methods.control}
                onClear={() => methods.setValue('memo', '')}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
                {__('Withdraw Cutoff Day')} <span className='text-[#FF4C51]'>*</span>
              </div>
              <div
                className={`grid ${methods.watch('withdraw_cutoff_option') === 'enter_date' ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}
              >
                <FormSelect
                  name='withdraw_cutoff_option'
                  placeholder={__('Choose Options')}
                  options={withdrawCutoffOptions}
                  height='36.6px'
                  onClear={() => {
                    methods.setValue('withdraw_cutoff_option', 'end_month')
                    methods.setValue('withdraw_cutoff_day', '')
                  }}
                />
                <div className={`${methods.watch('withdraw_cutoff_option') === 'enter_date' ? 'block' : 'hidden'}`}>
                  <FormInput
                    name='withdraw_cutoff_day'
                    placeholder={__('Enter Date')}
                    control={methods.control}
                    fullWidth
                    type='number'
                    slotProps={{
                      input: {
                        inputProps: {
                          min: 1,
                          max: 30,
                          step: 1
                        },
                        endAdornment: <span className='text-[#2F2B3D66] text-[14px] ml-2'>{__('Day')}</span>
                      }
                    }}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
        <div className='flex flex-row gap-4 mt-6'>
          <Button
            variant='contained'
            className='text-white text-[15px] font-medium leading-[22px] bg-[#3551F2] shadow-none rounded-full'
            type='submit'
          >
            {__('Submit')}
          </Button>
          <Button
            variant='contained'
            className='text-[#FF4C51] text-[15px] font-medium leading-[22px] bg-[#FCE2E3] shadow-none rounded-full'
            onClick={onClose}
          >
            {__('Cancel')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default EmployerManualForm
